import { baseApi } from './api';

export type EfficiencyResultType = {
    deliveryCount: number;
    deviceCount: number;
    deliveryAverage: number;
    moreRevenue: number;
    deliveryAvoided: number;
};

export type EfficiencyAccount = {
    /** Third-party backoffice accountId. @see Device accountId field. */
    accountId: string;
    /** The average delivery size. */
    average: number;
    /** The number of deliveries avoided. */
    avoided: number;
    /** The tank size. */
    capacity: number;
    /** The number of deliveries detected for this tank. */
    count: number;
    deviceId: string;
    /** Name of the associated tank. */
    name: string;
    /** The amount of savings MISSED. */
    saved: number;
    /** Customer associated with account/tank */
    customer_name: string;
};

export const efficiencyApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        getTrendSavingCalculationsData: builder.query<
            EfficiencyResultType,
            { supplierId: string | null; startDate: Date }
        >({
            query: ({ supplierId, startDate }) => {
                const aggregationQuery = {
                    query: {
                        bool: {
                            must: [
                                {
                                    term: {
                                        'device_data.supplier_id': {
                                            value: supplierId,
                                        },
                                    },
                                },
                                {
                                    range: {
                                        time: { gt: startDate.valueOf() },
                                    },
                                },
                            ],
                        },
                    },
                    aggs: {
                        devices: {
                            terms: {
                                field: 'device_id',
                                size: 100000,
                            },
                            aggs: {
                                sum_diff_for_device: {
                                    sum: {
                                        field: 'diff',
                                    },
                                },
                            },
                        },
                        avg_diff: {
                            avg: {
                                field: 'diff',
                            },
                        },
                        unique_devices: {
                            cardinality: {
                                field: 'device_id',
                            },
                        },
                    },
                };

                const data = {
                    json_query: aggregationQuery,
                    supplier_id: supplierId,
                    offset: 1,
                    limit: 1,
                    index: 'fillups',
                };

                return {
                    method: 'POST',
                    url: 'es/search/fillups',
                    body: data,
                };
            },
            transformResponse: (response: any) => {
                const data = {
                    deliveryCount: response.total,
                    deviceCount: response.aggregations.unique_devices.value,
                    deliveryAverage: response.aggregations.avg_diff.value,
                    moreRevenue:
                        (response.aggregations.avg_diff.value / 31 - 1) * 100,
                    deliveryAvoided:
                        response.aggregations.devices.buckets.reduce(
                            (sum: number, bucket: any) => {
                                const avoidedForDevice =
                                    bucket.sum_diff_for_device.value / 31 -
                                    bucket.doc_count;

                                if (avoidedForDevice > 0) {
                                    sum += avoidedForDevice;
                                }
                                return sum;
                            },
                            0
                        ),
                };

                return { ...data };
            },
        }),
        getTrendMonthData: builder.query<any, { supplierId: string }>({
            query: ({ supplierId }) => {
                const aggregationQuery = {
                    query: {
                        bool: {
                            must: [
                                {
                                    term: {
                                        'device_data.supplier_id': {
                                            value: supplierId,
                                        },
                                    },
                                },
                            ],
                        },
                    },
                    aggs: {
                        fillups_per_month: {
                            date_histogram: {
                                field: 'time',
                                interval: 'month',
                                min_doc_count: 0,
                                time_zone: new Date().getTimezoneOffset() / 60,
                            },
                            aggs: {
                                avg_diff_for_month: {
                                    avg: {
                                        field: 'diff',
                                    },
                                },
                            },
                        },
                    },
                };

                const body = {
                    json_query: aggregationQuery,
                    offset: 1,
                    limit: 1,
                };

                return {
                    method: 'POST',
                    url: 'es/search/fillups',
                    body,
                };
            },
            transformResponse: (response: any) => {
                const monthData = response?.aggregations?.fillups_per_month;
                const data = {
                    averages: monthData?.buckets?.map((bucket: any) => [
                        bucket.key,
                        Math.round(bucket.avg_diff_for_month.value),
                    ]),
                    counts: monthData?.buckets?.map((bucket: any) => [
                        bucket.key,
                        bucket.doc_count,
                    ]),
                };

                return { ...data };
            },
        }),
        getAccounts: builder.query<EfficiencyAccount[], { supplierId: string }>(
            {
                query: ({ supplierId }) => {
                    const aggregationQuery = {
                        query: {
                            bool: {
                                must: [
                                    {
                                        term: {
                                            'device_data.supplier_id': {
                                                value: supplierId,
                                            },
                                        },
                                    },
                                ],
                            },
                        },
                        aggs: {
                            accounts: {
                                terms: {
                                    field: 'device_id',
                                    size: 10000,
                                },
                                aggs: {
                                    tank_size: {
                                        terms: {
                                            field: 'capacity',
                                            size: 1,
                                        },
                                    },
                                    last_device_name: {
                                        top_hits: {
                                            sort: [{ time: { order: 'desc' } }],
                                            _source: [
                                                'device_name',
                                                'customer_name',
                                            ],
                                        },
                                    },
                                    account: {
                                        terms: {
                                            field: 'account_id',
                                            size: 1,
                                        },
                                    },
                                    sum_diff_for_device: {
                                        sum: {
                                            field: 'diff',
                                        },
                                    },
                                },
                            },
                        },
                    };

                    const body = {
                        json_query: aggregationQuery,
                        offset: 1,
                        limit: 1,
                    };

                    return {
                        method: 'POST',
                        url: 'es/search/fillups',
                        body,
                    };
                },
                transformResponse: (result: any) => {
                    const aggregations =
                        result?.aggregations?.accounts?.buckets;

                    if (!aggregations || !aggregations.length) {
                        return [];
                    }

                    const accounts = aggregations.map(
                        (agg: Record<string, any>) => {
                            const sumGallons =
                                (agg.sum_diff_for_device.value *
                                    agg.tank_size.buckets[0].key) /
                                100;
                            const sumDiff = agg.sum_diff_for_device.value;
                            const accountData: EfficiencyAccount = {
                                name: agg.last_device_name.hits.hits[0]._source
                                    .device_name,
                                customer_name:
                                    agg.last_device_name.hits.hits[0]._source
                                        .customer_name,
                                accountId: agg.account.buckets[0].key,
                                deviceId: agg.key,
                                count: agg.doc_count,
                                capacity: agg.tank_size.buckets[0].key,
                                average: sumDiff / agg.doc_count,
                                avoided: 0,
                                saved: 0,
                            };

                            const avoided =
                                sumGallons / (0.31 * accountData.capacity) -
                                accountData.count;
                            if (
                                !avoided ||
                                isNaN(avoided) ||
                                avoided < 0 ||
                                !isFinite(avoided)
                            ) {
                                accountData.avoided = 0;
                            } else {
                                accountData.avoided = avoided;
                            }

                            const saved =
                                Math.round(
                                    accountData.count -
                                        sumGallons /
                                            (0.6 * accountData.capacity)
                                ) * 60;
                            if (
                                !saved ||
                                isNaN(saved) ||
                                saved < 0 ||
                                !isFinite(saved)
                            ) {
                                accountData.saved = 0;
                            } else {
                                accountData.saved = saved;
                            }

                            return accountData;
                        }
                    );

                    return accounts;
                },
            }
        ),
    }),
});

export const {
    useLazyGetTrendSavingCalculationsDataQuery,
    useLazyGetTrendMonthDataQuery,
    useLazyGetAccountsQuery,
} = efficiencyApi;
